import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  Avatar,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
  Schedule as TimeIcon,
} from '@mui/icons-material';

const ChatHistoryModal = ({ open, onClose, chat }) => {
  if (!chat) return null;

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDisplayIntent = (intent) => {
    // Ocultar TODOS los intents - no mostrar ninguna burbuja
    return '';
  };

  const sortedMessages = chat.messages 
    ? [...chat.messages].sort((a, b) => {
        const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
        const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
        return dateA - dateB;
      })
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
        }}
      >
        <Box display="flex" alignItems="center">
          <Avatar
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              mr: 2,
              width: 48,
              height: 48,
            }}
          >
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {chat.phoneNumber || chat.id}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {sortedMessages.length} mensajes • Iniciado el{' '}
              {chat.createdAt ? formatMessageTime(chat.createdAt) : 'N/A'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {sortedMessages.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ py: 8 }}
          >
            <BotIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
              Sin mensajes
            </Typography>
            <Typography variant="body2" color="textSecondary">
              No hay mensajes en este chat aún
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {sortedMessages.map((message, index) => (
              <React.Fragment key={index}>
                <ListItem
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    py: 2,
                    px: 3,
                    backgroundColor: 'transparent',
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent={(message.source || message.sender) === 'user' ? 'flex-end' : 'flex-start'}
                    mb={1}
                  >
                    {(message.source || message.sender) === 'bot' && (
                      <Avatar
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 32,
                          height: 32,
                          mr: 2,
                          mt: 0.5,
                        }}
                      >
                        <BotIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                    )}
                    
                    <Box
                      sx={{
                        maxWidth: '70%',
                        backgroundColor: 'transparent',
                        color: 'text.primary',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                        position: 'relative',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          wordBreak: 'break-word',
                          lineHeight: 1.5,
                          mb: message.intent ? 1 : 0,
                        }}
                      >
                        {message.message}
                      </Typography>
                      
                      {message.intent && getDisplayIntent(message.intent) && (
                        <Chip
                          label={getDisplayIntent(message.intent)}
                          size="small"
                          sx={{
                            backgroundColor: 'transparent',
                            border: '1px solid',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            fontSize: '0.7rem',
                            height: 20,
                            mt: 0.5,
                          }}
                        />
                      )}
                    </Box>

                    {(message.source || message.sender) === 'user' && (
                      <Avatar
                        sx={{
                          bgcolor: 'secondary.main',
                          color: 'white',
                          width: 32,
                          height: 32,
                          ml: 2,
                          mt: 0.5,
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                    )}
                  </Box>

                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={(message.source || message.sender) === 'user' ? 'flex-end' : 'flex-start'}
                    sx={{ 
                      pl: (message.source || message.sender) === 'bot' ? 6 : 0,
                      pr: (message.source || message.sender) === 'user' ? 6 : 0,
                    }}
                  >
                    <TimeIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5 }} />
                    <Typography variant="caption" color="text.secondary">
                      {formatMessageTime(message.timestamp)}
                    </Typography>
                  </Box>
                </ListItem>
                {index < sortedMessages.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Box display="flex" alignItems="center">
            <Chip
              icon={<TimeIcon />}
              label={`Último mensaje: ${chat.lastMessageAt ? formatMessageTime(chat.lastMessageAt) : 'N/A'}`}
              variant="outlined"
              size="small"
              sx={{ mr: 2 }}
            />
            <Chip
              label={`${sortedMessages.length} mensajes`}
              color="primary"
              size="small"
            />
          </Box>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cerrar
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ChatHistoryModal;
